import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// Same pattern as AdminUsers.js's ProofAPI — payment-proof routes live at
// /api/payment-proof (not nested under /api/admin), but the list/approve/
// reject sub-routes are still adminMiddleware-protected, so we still need
// to attach the admin token here.
const ProofAPI = axios.create({
  baseURL: "https://alkebulanfkalertbackend.onrender.com/api/payment-proof",
});
ProofAPI.interceptors.request.use((req) => {
  const token = localStorage.getItem("adminToken");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

const AdminAPIBase = axios.create({
  baseURL: "https://alkebulanfkalertbackend.onrender.com/api/admin",
});
AdminAPIBase.interceptors.request.use((req) => {
  const token = localStorage.getItem("adminToken");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

// Card applications — /api/cards/admin/* routes, admin-token protected.
const CardAPI = axios.create({
  baseURL: "https://alkebulanfkalertbackend.onrender.com/api/cards",
});
CardAPI.interceptors.request.use((req) => {
  const token = localStorage.getItem("adminToken");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

// ---- Nav structure: grouped instead of one flat list ----
const NAV_GROUPS = [
  {
    label: null,
    items: [{ to: "/admin/overview", label: "Overview", icon: "📊" }],
  },
  {
    label: "Users & Payments",
    items: [
      { to: "/admin/users", label: "Manage Users", icon: "👥", badgeKey: "pending" },
      { to: "/admin/cards", label: "Card Applications", icon: "💳", badgeKey: "cards" },
      { to: "/admin/credit", label: "Credit User", icon: "💳" },
      { to: "/admin/withdrawals", label: "Withdrawals", icon: "💸" },
      { to: "/admin/vendors", label: "Manage Vendors", icon: "🏪" },
      { to: "/admin/vendor-purchases", label: "Vendor Purchases", icon: "🛒" },
    ],
  },
  {
    label: "Communication",
    items: [
      { to: "/admin/tickets", label: "Support Tickets", icon: "🎫" },
      { to: "/admin/chat", label: "Chat with Users", icon: "💬" },
      { to: "/admin/send-notification", label: "Send Notification", icon: "🔔" },
      { to: "/admin/send-email", label: "Send Email", icon: "✉️" },
      { to: "/admin/broadcast", label: "Broadcast Email", icon: "📩" },
    ],
  },
];

const AdminDashboard = () => {
  const admin = JSON.parse(localStorage.getItem("adminUser") || "null");
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingCards, setPendingCards] = useState(0);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const [proofsRes, usersRes] = await Promise.all([
          ProofAPI.get("/", { params: { status: "pending" } }),
          AdminAPIBase.get("/users"),
        ]);
        const pendingProofs = Array.isArray(proofsRes.data) ? proofsRes.data.length : 0;
        const pendingUsers = Array.isArray(usersRes.data)
          ? usersRes.data.filter((u) => (u.status || "pending") === "pending").length
          : 0;
        setPendingCount(pendingProofs + pendingUsers);
      } catch (err) {
        console.error("Pending count fetch failed:", err.message);
      }
    };

    const fetchPendingCards = async () => {
      try {
        const res = await CardAPI.get("/admin/pending");
        setPendingCards(Array.isArray(res.data?.cards) ? res.data.cards.length : 0);
      } catch (err) {
        console.error("Pending cards fetch failed:", err.message);
      }
    };

    fetchPendingCount();
    fetchPendingCards();
    const interval = setInterval(() => {
      fetchPendingCount();
      fetchPendingCards();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigate("/login");
  };

  const renderNavItem = (item) => {
    const badgeCount =
      item.badgeKey === "pending"
        ? pendingCount
        : item.badgeKey === "cards"
        ? pendingCards
        : 0;
    return (
      <Link
        key={item.to}
        to={item.to}
        className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
          isActive(item.to)
            ? "bg-indigo-600 text-white font-semibold shadow-sm"
            : "text-gray-300 hover:bg-gray-800 hover:text-white"
        }`}
      >
        <span className="flex items-center gap-2.5">
          <span className="text-base">{item.icon}</span>
          {item.label}
        </span>
        {badgeCount > 0 && (
          <span className="inline-flex items-center justify-center bg-red-600 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5">
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </Link>
    );
  };

  const sidebarContent = (
    <>
      <div className="p-6 text-xl font-bold border-b border-gray-800 flex items-center gap-2">
        <span>⚡</span> Admin Panel
      </div>

      <nav className="flex-1 p-4 space-y-6 overflow-y-auto">
        {NAV_GROUPS.map((group, i) => (
          <div key={i}>
            {group.label && (
              <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                {group.label}
              </p>
            )}
            <div className="space-y-1">
              {group.items.map(renderNavItem)}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-gray-800">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium transition-colors"
        >
          🚪 Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Desktop sidebar — static, always visible at md+ */}
      <aside className="hidden md:flex w-64 flex-shrink-0 bg-gray-900 text-white flex-col">
        {sidebarContent}
      </aside>

      {/* Mobile sidebar — slide-in drawer */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="relative w-64 h-full bg-gray-900 text-white flex flex-col shadow-2xl">
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="bg-white shadow p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* Hamburger — mobile only */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 text-xl"
              aria-label="Open menu"
            >
              ☰
            </button>
            <h1 className="text-lg sm:text-xl font-semibold truncate">
              Admin Dashboard
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {pendingCards > 0 && (
              <span className="hidden sm:inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-medium px-3 py-1.5 rounded-full border border-amber-200">
                💳 {pendingCards} card{pendingCards > 1 ? "s" : ""} pending
              </span>
            )}
            {pendingCount > 0 && (
              <span className="hidden sm:inline-flex items-center gap-1.5 bg-red-50 text-red-700 text-xs font-medium px-3 py-1.5 rounded-full border border-red-200">
                🔴 {pendingCount} pending
              </span>
            )}
            <span className="text-gray-600 text-sm truncate max-w-[140px] sm:max-w-none">
              {admin?.name || "Admin"}
            </span>
          </div>
        </header>

        <div className="flex-1 p-4 sm:p-6 overflow-x-hidden">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
